/* eslint-disable react-refresh/only-export-components */
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { ButtonHTMLAttributes, forwardRef } from 'react';

const buttonVariants = cva<ButtonVariant>(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutrals-N500 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'bg-primary-blue-B600 font-semibold text-white hover:bg-primary-blue-B700 disabled:bg-neutrals-N300 disabled:hover:bg-neutrals-N300 disabled:hover:cursor-not-allowed disabled:text-neutrals-N700',
        secondary:
          'bg-[#545e83] text-white font-semibold hover:bg-[#000e46] disabled:bg-neutrals-N300 disabled:hover:bg-neutrals-N300 disabled:hover:cursor-not-allowed disabled:text-neutrals-N700',
        destructive:
          'bg-primary-red-R400 text-white hover:bg-primary-red-R600 disabled:bg-neutrals-N300 disabled:hover:bg-neutrals-N300 disabled:hover:cursor-not-allowed disabled:text-neutrals-N700',
        outline:
          'border border-primary-blue-B600 font-semibold bg-inherit hover:bg-primary-blue-B600 hover:text-white text-primary-blue-B600 disabled:border-neutrals-N300 disabled:hover:bg-inherit disabled:hover:cursor-not-allowed disabled:text-neutrals-N700',
        ghost:
          'hover:bg-neutrals-N300 disabled:hover:bg-inherit font-semibold disabled:text-neutrals-N700 disabled:hover:cursor-not-allowed',
        'ghost-primary':
          'hover:bg-neutrals-N300 disabled:hover:bg-inherit font-semibold text-primary-blue-B600 disabled:text-neutrals-N700 disabled:hover:cursor-not-allowed',
        'ghost-destructive':
          'hover:bg-neutrals-N200 disabled:hover:bg-inherit font-semibold text-primary-red-R400 disabled:text-neutrals-N700 hover:text-primary-red-R500 disabled:hover:cursor-not-allowed',
        link: 'text-black underline-offset-4 hover:underline font-semibold disabled:text-neutrals-N700 disabled:underline disabled:hover:cursor-not-allowed',
        'outline-secondary':
          'border border-neutrals-N400 font-semibold bg-inherit hover:bg-neutrals-N300 text-neutrals-N900 disabled:border-neutrals-N400 disabled:hover:bg-inherit disabled:hover:cursor-not-allowed disabled:text-neutrals-N700',
        icon: 'hover:bg-neutrals-N300 font-semibold disabled:hover:bg-inherit disabled:text-neutrals-N700 disabled:hover:cursor-not-allowed',
        circle: 'bg-neutrals-N200 p-2 rounded-full hover:bg-neutrals-N300',
      },
      size: {
        default: 'h-10 px-3 py-2',
        xs: 'h-6 w-6',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        circle: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  size?: Size;
  variant?: Variant;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export type Size = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'circle';

export type Variant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'outline-secondary'
  | 'destructive'
  | 'ghost'
  | 'ghost-primary'
  | 'ghost-destructive'
  | 'link'
  | 'icon'
  | 'circle';

export type ButtonVariant = {
  variant: Record<Variant, string>;
  size: Record<Size, string>;
};

export { Button, buttonVariants };
